import React, { useEffect, useState } from 'react';
import { Button, Card, CardContent, Typography } from '@mui/material';
import toast from 'react-hot-toast';

import {
  AdminBranchWorkerService,
  BranchWorkerStatusIds,
  BranchWorkerStatuses,
  BranchWorkerAccountTypes,
  BranchWorkerAccountTypeIds,
  CreditCard,
  CreditCardService,
  Parent,
  ParentPlanService,
  Sitter,
  SitterBranchWorkerService,
  SitterBranchWorker,
  SitterPaymentSettings,
  SitterPlanService,
  SitterPaymentSettingsService,
  UserPlan,
} from 'us-web-services';

import useAuthentication from '../../store/useAuthentication';
import CardStyles from '../../styles/CardStyles';
import DateUtil from '../../util/DateUtil';
import ConfirmDialog from '../common/ConfirmDialog';

interface Props {
  profile: Parent | Sitter;
  userId: number;
  isParent: boolean;
}

function PaymentInfo({ profile, userId, isParent }: Props) {
  const { classes } = CardStyles();
  const authenticationState = useAuthentication()[0];
  const adminUser = authenticationState.authentication.user;

  const [branchWorker, setBranchWorker] = useState<SitterBranchWorker>({});
  const [creditCard, setCreditCard] = useState<CreditCard>({});
  const [openDeleteBranchWorkerDialog, setOpenDeleteBranchWorkerDialog] = useState(false);
  const [openRemoveCardDialog, setOpenRemoveCardDialog] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState<SitterPaymentSettings>({ id: userId });
  const [plan, setPlan] = useState<UserPlan>({});

  const refreshBranchWorker = async () => {
    const config = {
      params: {
        sitterId: userId,
        userId,
      },
    };
    const branchWorkerResponse = (await SitterBranchWorkerService.getByFilter(config)).data;

    setBranchWorker(branchWorkerResponse.data.length ? branchWorkerResponse.data[0] : {});
  };

  useEffect(() => {
    const getCreditCard = async () => {
      const contentConfig = {
        params: {
          userId,
          active: true,
        },
      };
      const creditCardResponse = (await CreditCardService.findByFilter(contentConfig)).data;

      setCreditCard(
        creditCardResponse.data.length
          ? creditCardResponse.data[0]
          : {},
      );
    };

    const getParentPlans = async () => {
      const config = {
        params: {
          active: true,
          parentId: userId,
          userId,
        },
      };

      ParentPlanService.getByFilter(config).then(response => {
        setPlan(response.data.data.length ? response.data.data[0] : {});
      });
    };

    const getPaymentSettings = async () => {
      SitterPaymentSettingsService.findById(userId).then(response => {
        setPaymentSettings(response.data.data);
      });
    };

    const getSitterPlans = async () => {
      const config = {
        params: {
          active: true,
          sitterId: userId,
          userId,
        },
      };

      SitterPlanService.getByFilter(config).then(response => {
        setPlan(response.data.data.length ? response.data.data[0] : {});
      });
    };

    if (isParent) {
      getParentPlans();
    } else {
      getSitterPlans();
      refreshBranchWorker();
      getPaymentSettings();
    }
    getCreditCard();
  }, [userId, isParent]);

  const isPaymentAdmin = () => adminUser.roles.filter(role => role.code === 'PAYMENT_ADMIN').length > 0;

  const togglePaymentSettings = async () => {
    const body = {
      id: paymentSettings.id,
      paymentDisabled: !paymentSettings.paymentDisabled,
    };

    const updatedPaymentSettings = (await SitterPaymentSettingsService.patch(userId, body)).data.data;

    toast.success(`Credit card bookings ${updatedPaymentSettings.paymentDisabled ? 'disabled' : 'enabled'}`);
    setPaymentSettings(updatedPaymentSettings);
  };

  const removeCard = async () => {
    await CreditCardService.delete(creditCard.id);
    setCreditCard({});
    setOpenRemoveCardDialog(false);
  };

  const openCardDialog = () => {
    setOpenRemoveCardDialog(true);
  };

  const deleteBranchWorker = async () => {
    await AdminBranchWorkerService.delete(branchWorker?.sitterId);
    setBranchWorker({});
    setOpenDeleteBranchWorkerDialog(false);
  };

  const openBranchWorkerDialog = () => {
    setOpenDeleteBranchWorkerDialog(true);
  };

  return (
    <>
      <ConfirmDialog
        confirmAction={removeCard}
        open={openRemoveCardDialog}
        setOpen={setOpenRemoveCardDialog}
        title='Confirm Remove Payment Method'
        text='Are you sure you want to delete this card?'
      />
      <ConfirmDialog
        confirmAction={deleteBranchWorker}
        open={openDeleteBranchWorkerDialog}
        setOpen={setOpenDeleteBranchWorkerDialog}
        title='Confirm Delete Branch Worker'
        text='Are you sure you want to delete this branch worker? This cannot be undone.'
      />
      <Card className={classes.sectionCard}>
        <CardContent className={classes.sectionContent}>
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 280, flex: 1 }}>
              <Typography className={classes.sectionHeader}>
                Payment Method
              </Typography>
              {creditCard.id ? (
                <>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Provider</span>
                    <span className={classes.infoValue}>{creditCard.customerId === null ? 'Braintree' : 'Stripe'}</span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Card Type</span>
                    <span className={classes.infoValue}>{creditCard.type}</span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Last 4 Digits</span>
                    <span className={classes.infoValue}>{creditCard.number}</span>
                  </div>
                  <Button
                    variant='outlined'
                    color='error'
                    size='small'
                    onClick={openCardDialog}
                    className={classes.actionButton}
                  >
                    Remove Card
                  </Button>
                </>
              ) : (
                <Typography variant='body2' color='text.secondary'>No credit card linked</Typography>
              )}

              <Typography className={classes.sectionHeader} sx={{ mt: 3 }}>
                Payout Method
              </Typography>
              {branchWorker?.id ? (
                <>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Provider</span>
                    <span className={classes.infoValue}>Branch</span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Status</span>
                    <span className={classes.infoValue}>
                      {BranchWorkerStatuses[BranchWorkerStatusIds[branchWorker.statusId]]}
                    </span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Account Type</span>
                    <span className={classes.infoValue}>
                      {BranchWorkerAccountTypes[BranchWorkerAccountTypeIds[branchWorker.accountType]]}
                    </span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Worker ID</span>
                    <span className={classes.infoValue}>{branchWorker.workerId}</span>
                  </div>
                  <div>
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={refreshBranchWorker}
                      className={classes.actionButton}
                    >
                      Refresh Status
                    </Button>
                    <Button
                      variant='outlined'
                      color='error'
                      size='small'
                      onClick={openBranchWorkerDialog}
                      className={classes.actionButton}
                    >
                      Delete Worker
                    </Button>
                  </div>
                </>
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  {isPaymentAdmin() ? 'TODO: <AddBranchAccount />' : 'No Branch Account'}
                </Typography>
              )}

              {!isParent && (
                <>
                  <Typography className={classes.sectionHeader} sx={{ mt: 3 }}>
                    Credit Card Bookings
                  </Typography>
                  <Button
                    variant='contained'
                    size='small'
                    color={paymentSettings.paymentDisabled ? 'primary' : 'error'}
                    onClick={togglePaymentSettings}
                    className={classes.actionButton}
                  >
                    {paymentSettings.paymentDisabled ? 'Enable' : 'Disable'} Credit Card Bookings
                  </Button>
                </>
              )}
            </div>

            <div style={{ minWidth: 280, flex: 1 }}>
              <Typography className={classes.sectionHeader}>
                Membership Plan
              </Typography>
              {plan.id ? (
                <>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Plan</span>
                    <span className={classes.infoValue}>{plan.plan.name}</span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Plan ID</span>
                    <span className={classes.infoValue}>{plan.plan.id}</span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Start Date</span>
                    <span className={classes.infoValue}>{DateUtil.getReadableDate(plan.start)}</span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>End Date</span>
                    <span className={classes.infoValue}>{DateUtil.getReadableDate(plan.end)}</span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>Price</span>
                    <span className={classes.infoValue}>${plan.fee}</span>
                  </div>
                </>
              ) : (
                <Typography variant='body2' color='text.secondary'>No membership plan found</Typography>
              )}
              {!isParent && (
                <div className={classes.infoRow} style={{ marginTop: 16 }}>
                  <span className={classes.infoLabel}>Accepts CC</span>
                  <span className={classes.infoValue}>{(profile as Sitter).acceptsCreditCards ? 'Yes' : 'No'}</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

export default PaymentInfo;
